/**
 * @module engine/CanvasEngine
 * @summary The orchestrator: owns the canvas lifecycle, the RAF render loop,
 *   the public command API, and the engine→React event bridge.
 * @description
 *   React mounts the engine once (via useCanvasEngine), then drives it through
 *   imperative method calls (setTool, zoomIn, applyProp, …) and listens to
 *   coarse events (`on('selection', …)`). The engine NEVER calls React.
 *
 *   `mount`/`unmount` are idempotent so React 18 StrictMode's double-invoke in
 *   dev fully tears down and re-creates the RAF loop + listeners (no leaks /
 *   doubled loops).
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 1010-1030 (canvas +
 *   resize + loop), 3695-3740 (tool/zoom), 3646-3656 (label edit), 4077-4099
 *   (minimap nav/toggle), 4277-4290 (init).
 */
import {
  S,
  on,
  emit,
  markDirty,
  makeInitialState,
} from './state';
import type { EngineEvent } from '../types';
import type {
  ElementSpec,
  FeedItem,
  Formulation,
  OperatingPoint,
  ScrewConfig,
  SelectionSummary,
  Shape,
  Tool,
} from '../types';
import { toW, toS, getR } from './coords';
import { drawAll, getWorldBounds, setMinimapCollapsed, isMinimapCollapsed } from './render';
import { undo, redo, canUndo, canRedo, pushUndo } from './history';
import { mkShape } from './factory';
import { registry } from './shapes/registry';
import {
  doCopy,
  doPaste,
  doDuplicate,
  doDelete,
  clearAll,
  selectAll,
} from './ops/editOps';
import {
  groupSelected,
  ungroupSelected,
  rotateSelected,
  setRotation,
  alignSelected,
  distributeSelected,
  bringToFront,
  sendToBack,
  type AlignMode,
  type DistAxis,
} from './ops/transformOps';
import { attachPointerControls, type PointerCallbacks } from './input/PointerController';
import { attachKeyboardControls, setShortcutsSuspended } from './input/KeyboardController';
import { attachScrollbarControls } from './input/ScrollbarController';
import {
  tryAutoRestore,
  hasUnsavedChanges,
  doAutoSave,
  scheduleAutoSave,
  markSaved,
  clearAutoSave,
} from './persistence';
import { syncCatsFromApi } from '../lib/categories';
import { saveJson, loadJsonFile } from './export/json';
import { exportPng } from './export/exportPng';
import { exportPdf, type PdfExportOptions } from './export/exportPdf';
import { toast } from '../lib/toast';
import { buildRecipes, type Recipe } from './formulation';
import { makeElement } from './sim/geometry';

/** Status snapshot consumed by the status bar / toolbar. */
export interface StatusSnapshot {
  coordsX: number;
  coordsY: number;
  shapeCount: number;
  connCount: number;
  tool: Tool;
  selLabel: string;
}

class CanvasEngine {
  private raf = 0;
  private mounted = false;
  private detachers: Array<() => void> = [];
  private ro: ResizeObserver | null = null;
  private editShape: Shape | null = null;
  /** Set by React so the engine can open the context menu UI. */
  contextMenuHandler:
    | ((info: { clientX: number; clientY: number; hasSelection: boolean; hasClipboard: boolean }) => void)
    | null = null;

  // ── Lifecycle ───────────────────────────────────────────────────────

  /** Bind the engine to a canvas + its scroll wrapper and start the loop. */
  mount(canvas: HTMLCanvasElement, wrap: HTMLElement): void {
    if (this.mounted) this.unmount();
    S.canvas = canvas;
    S.ctx = canvas.getContext('2d');
    S.wrap = wrap;
    this.mounted = true;

    this.resize();
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(wrap);
    // DPR can change when a window moves between monitors.
    window.addEventListener('resize', this.onWindowResize);
    // Warn before closing the tab with edits not yet saved to a JSON file.
    window.addEventListener('beforeunload', this.onBeforeUnload);

    const pointerCb: PointerCallbacks = {
      openLabelEdit: (s, sx, sy) => this.openLabelEdit(s, sx, sy),
      openContextMenu: (info) => this.contextMenuHandler?.(info),
      updateZoomLabel: () => emit('view'),
    };
    this.detachers.push(attachPointerControls(canvas, pointerCb));
    this.detachers.push(
      attachKeyboardControls({
        setTool: (t) => this.setTool(t),
        requestSave: () => this.save(),
        openLabelEdit: (s, sx, sy) => this.openLabelEdit(s, sx, sy),
        zoomIn: () => this.zoomIn(),
        zoomOut: () => this.zoomOut(),
        zoomReset: () => this.zoomReset(),
        fit: () => this.fit(),
      }),
    );
    this.detachers.push(attachScrollbarControls());
    this.bindLabelInput();
    this.bindMinimap();

    // First-run content: load custom symbols (localStorage cache first, then API sync),
    // then restore last saved session if available.
    // 'library' refreshes the shape palette; emitted after the local load, after
    // the background API symbol sync (setOnSync), and after the category sync.
    registry.setOnSync(() => { emit('library'); });
    registry.loadCustom();
    tryAutoRestore();
    // Background: sync custom categories from API, then refresh the palette.
    syncCatsFromApi().then(() => emit('library')).catch(() => {});
    emit('library');
    emit('doc');
    emit('selection');
    emit('history');
    emit('view');

    this.loop();
  }

  /** Stop the loop, detach everything, release the canvas. Idempotent. */
  unmount(): void {
    if (!this.mounted) return;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.detachers.forEach((d) => d());
    this.detachers = [];
    this.ro?.disconnect();
    this.ro = null;
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    S.canvas = null;
    S.ctx = null;
    S.wrap = null;
    this.mounted = false;
  }

  private onWindowResize = () => this.resize();

  private onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!hasUnsavedChanges()) return;
    // Flush the pending debounce so the restore prompt has the latest state.
    doAutoSave();
    e.preventDefault();
    e.returnValue = '';
  };

  /** DPR-aware canvas resize. World logic stays in CSS pixels. */
  resize(): void {
    const { wrap, canvas } = S;
    if (!wrap || !canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = wrap.clientWidth;
    const cssH = wrap.clientHeight;
    S.CW = cssW;
    S.CH = cssH;
    S.dpr = dpr;
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    canvas.width = Math.max(1, Math.round(cssW * dpr));
    canvas.height = Math.max(1, Math.round(cssH * dpr));
    markDirty();
  }

  private loop = (): void => {
    if (S.dirty) {
      drawAll();
      S.dirty = false;
    }
    this.raf = requestAnimationFrame(this.loop);
  };

  // ── Event bridge (re-exported for React) ────────────────────────────
  on(evt: EngineEvent, cb: () => void): () => void {
    return on(evt, cb);
  }

  // ── Tool / view ─────────────────────────────────────────────────────
  /** Suspend canvas shortcuts while a full-screen overlay/modal is open. */
  setShortcutsEnabled(enabled: boolean): void {
    setShortcutsSuspended(!enabled);
  }

  setTool(t: Tool): void {
    S.tool = t;
    if (t !== 'connect') {
      S.isConnecting = false;
      S.connectFrom = null;
      S.connectPreview = null;
    }
    if (S.canvas)
      S.canvas.style.cursor = t === 'pan' ? 'grab' : t === 'connect' ? 'crosshair' : 'default';
    markDirty();
    emit('tool');
  }
  getTool(): Tool {
    return S.tool;
  }

  /** Set zoom keeping the viewport centre fixed (like wheel-zoom at cursor). */
  private zoomTo(nz: number): void {
    const z = Math.min(4, Math.max(0.2, nz));
    const cx = S.CW / 2;
    const cy = S.CH / 2;
    S.panX = cx - (cx - S.panX) * (z / S.zoom);
    S.panY = cy - (cy - S.panY) * (z / S.zoom);
    S.zoom = z;
    emit('view');
    markDirty();
  }
  zoomIn(): void {
    this.zoomTo(S.zoom * 1.2);
  }
  zoomOut(): void {
    this.zoomTo(S.zoom / 1.2);
  }
  zoomReset(): void {
    S.zoom = 1;
    S.panX = 60;
    S.panY = 40;
    emit('view');
    markDirty();
  }
  fit(): void {
    if (!S.shapes.length) {
      this.zoomReset();
      return;
    }
    const xs = S.shapes.map((s) => s.x);
    const ys = S.shapes.map((s) => s.y);
    const xe = S.shapes.map((s) => s.x + s.w);
    const ye = S.shapes.map((s) => s.y + s.h);
    const mx = Math.min(...xs);
    const my = Math.min(...ys);
    const bw = Math.max(...xe) - mx;
    const bh = Math.max(...ye) - my;
    const cw = S.CW - 60;
    const ch = S.CH - 60;
    S.zoom = Math.min((0.92 * cw) / (bw || 1), (0.92 * ch) / (bh || 1), 3);
    S.panX = cw / 2 - (mx + bw / 2) * S.zoom + 30;
    S.panY = ch / 2 - (my + bh / 2) * S.zoom + 30;
    emit('view');
    markDirty();
  }
  getZoomPct(): number {
    return Math.round(S.zoom * 100);
  }

  toggleSnap(): boolean {
    S.snapOn = !S.snapOn;
    emit('view');
    return S.snapOn;
  }
  toggleGrid(): boolean {
    S.gridOn = !S.gridOn;
    markDirty();
    emit('view');
    return S.gridOn;
  }
  toggleCurve(): boolean {
    S.routeMode = S.routeMode === 'manhattan' ? 'curved' : 'manhattan';
    toast('Routing: ' + S.routeMode);
    markDirty();
    // Routing is saved with the drawing, so this is an edit, not a view change.
    scheduleAutoSave();
    emit('view');
    emit('doc');
    return S.routeMode === 'curved';
  }
  getViewFlags() {
    return { snapOn: S.snapOn, gridOn: S.gridOn, curved: S.routeMode === 'curved' };
  }

  // ── History ─────────────────────────────────────────────────────────
  undo(): void {
    undo();
  }
  redo(): void {
    redo();
  }
  canUndo(): boolean {
    return canUndo();
  }
  canRedo(): boolean {
    return canRedo();
  }

  // ── Edit / transform ops (thin pass-throughs) ───────────────────────
  copy(): void {
    doCopy();
  }
  paste(): void {
    doPaste();
  }
  duplicate(): void {
    doDuplicate();
  }
  delete(): void {
    doDelete();
  }
  clear(): void {
    clearAll();
  }
  selectAll(): void {
    selectAll();
  }
  group(): void {
    groupSelected();
  }
  ungroup(): void {
    ungroupSelected();
  }
  rotate(deg: number): void {
    rotateSelected(deg);
  }
  setRotation(deg: number): void {
    setRotation(deg);
  }
  align(mode: AlignMode): void {
    alignSelected(mode);
  }
  distribute(axis: DistAxis): void {
    distributeSelected(axis);
  }
  bringToFront(): void {
    bringToFront();
  }
  sendToBack(): void {
    sendToBack();
  }
  toggleLock(): void {
    if (!S.selected.length) return;
    pushUndo();
    S.selected.forEach((id) => {
      const s = S.shapes.find((x) => x.id === id);
      if (s) s.locked = !s.locked;
    });
    markDirty();
    toast('Lock toggled');
  }
  resetRoute(): void {
    if (!S.selected.length) return;
    pushUndo();
    S.selected.forEach((id) => {
      const cn = S.connections.find((x) => x.id === id);
      if (cn) {
        delete cn.midX;
        delete cn.midY;
      }
    });
    markDirty();
    toast('Connection routing reset');
  }

  // ── Properties editing ──────────────────────────────────────────────
  /** Coalesces a burst of edits to one field into a single undo snapshot. */
  private lastProp = { key: '', t: 0 };

  /** Live-edit a property of the WHOLE selection (one undo step per field). */
  applyProp(field: string, value: string): void {
    if (!S.selected.length) return;
    // One snapshot per (selection, field) burst — typing a label letter by
    // letter or dragging a color picker stays a single undo step.
    const key = S.selected.join(',') + ':' + field;
    const now = Date.now();
    if (key !== this.lastProp.key || now - this.lastProp.t > 800) pushUndo();
    this.lastProp = { key, t: now };

    S.selected.forEach((id) => {
      const shape = S.shapes.find((x) => x.id === id);
      const conn = S.connections.find((x) => x.id === id);
      const target = shape || conn;
      if (!target) return;
      const t = target as unknown as Record<string, unknown>;
      switch (field) {
        case 'label':
        case 'tag':
        case 'notes':
        case 'labelPos':
          t[field] = value;
          break;
        case 'fg': {
          // The finished-good code. Blank removes it rather than storing an
          // empty string, so "not named yet" stays distinguishable from "named
          // nothing", and the line falls back to naming the FG by its machine.
          const code = value.trim();
          if (shape && code) shape.fg = code;
          else if (shape) delete shape.fg;
          break;
        }
        case 'fillColor':
          if (shape) shape.fillColor = value;
          break;
        case 'stroke': // shapes → strokeColor, connections → color
          if (conn) conn.color = value;
          else if (shape) shape.strokeColor = value;
          break;
        case 'strokeWidth':
          // Shapes store strokeWidth; connections store lineWidth.
          if (conn) conn.lineWidth = parseFloat(value) || 1.5;
          else if (shape) shape.strokeWidth = parseFloat(value) || 1.5;
          break;
        case 'fontSize':
          if (shape) shape.fontSize = parseFloat(value) || 10;
          break;
        case 'lineStyle':
          if (conn) (conn as unknown as Record<string, unknown>).lineStyle = value;
          break;
        case 'arrowHead':
          if (conn) (conn as unknown as Record<string, unknown>).arrowHead = value;
          break;
        case 'routeMode':
          if (conn) (conn as unknown as Record<string, unknown>).routeMode = value;
          break;
        case 'rate': {
          // Blank, zero or nonsense removes the figure rather than storing a
          // zero: "this feeds nothing in" and "nobody has said yet" are
          // different, and only the second should let the balance work it out.
          const kgh = parseFloat(value);
          if (Number.isFinite(kgh) && kgh > 0) t.rate = kgh;
          else delete t.rate;
          break;
        }
        case 'x':
        case 'y':
          if (shape) (shape as unknown as Record<string, number>)[field] = parseFloat(value) || 0;
          break;
        case 'w':
        case 'h':
          if (shape) (shape as unknown as Record<string, number>)[field] = Math.max(20, parseFloat(value) || 20);
          break;
      }
    });
    scheduleAutoSave();
    markDirty();
    // A throughput changes the 3D view's pipe labels, which are baked into the
    // compiled scene — 'status' alone would leave them showing the old figure.
    emit(field === 'rate' ? 'doc' : 'status');
  }

  // ── Formulation ─────────────────────────────────────────────────────
  /** The recipes on this drawing. */
  getFormulations(): Formulation[] {
    return S.formulations;
  }

  /** Each line's sheet, with the kilograms and flow colours worked out. */
  getRecipes(): Recipe[] {
    return buildRecipes(S.shapes);
  }

  /** Start a new line. Returns its id so the caller can focus its name field. */
  addFormulation(): string {
    pushUndo();
    const id = 'f' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    S.formulations.push({ id, fg: '', totalKgh: 1000, lines: [] });
    markDirty();
    emit('doc');
    return id;
  }

  /**
   * Change a line: its code, its rate, or its recipe.
   *
   * The whole recipe at once rather than one row at a time, for the same reason
   * `setFeed` takes the whole list — a caller that has to keep an index in step
   * with the model is a caller that will eventually edit the wrong row.
   */
  patchFormulation(id: string, patch: Partial<Omit<Formulation, 'id'>>): void {
    const f = S.formulations.find((x) => x.id === id);
    if (!f) return;
    // One undo step per burst, so typing a percentage is not twenty steps.
    const key = id + ':' + Object.keys(patch).join(',');
    const now = Date.now();
    if (key !== this.lastProp.key || now - this.lastProp.t > 800) pushUndo();
    this.lastProp = { key, t: now };

    if (patch.fg !== undefined) f.fg = patch.fg;
    if (patch.description !== undefined) f.description = patch.description;
    if (patch.totalKgh !== undefined) f.totalKgh = Math.max(0, patch.totalKgh);
    if (patch.lines !== undefined)
      f.lines = patch.lines.filter((l) => l && l.part).map((l) => ({ part: l.part, pct: l.pct }));
    markDirty();
    emit('doc');
  }

  /**
   * Remove a line, and unbind everything that was on it.
   *
   * Leaving shapes pointing at a formulation that no longer exists would make
   * them silently fall back to the no-line behaviour, which looks like the
   * drawing quietly forgetting its rates.
   */
  deleteFormulation(id: string): void {
    if (!S.formulations.some((f) => f.id === id)) return;
    pushUndo();
    S.formulations = S.formulations.filter((f) => f.id !== id);
    for (const s of S.shapes)
      if (s.formulationId === id) {
        delete s.formulationId;
        delete s.feed;
      }
    markDirty();
    emit('doc');
    emit('selection');
  }

  // ── Screw configurations ────────────────────────────────────────────
  //
  // A machine on the shop floor, saved with the drawing so it travels with the
  // `.json` rather than living on one engineer's laptop. Same shape of API as
  // the formulations above, and for the same reason: the panel edits whole
  // objects, never an index into a list it has to keep in step.

  /** The co-kneader machines defined on this drawing. */
  getScrewConfigs(): ScrewConfig[] {
    return S.screwConfigs;
  }

  /** Start a new machine. Returns its id so the caller can focus its name field. */
  addScrewConfig(line = '', seed?: Partial<ScrewConfig>): string {
    pushUndo();
    const id = 'sc' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    S.screwConfigs.push({
      id,
      line,
      machine: seed?.machine ?? {
        name: line || 'new machine',
        diameterMm: 70,
        rootDiameterMm: 44,
        strokeMm: 14,
        strokesPerRev: 1,
        motorKw: 75,
        ratedTorqueNm: 2400,
        baseSpeedRpm: 300,
        driveEfficiency: 0.92,
        noLoadKw: 6,
        // Four BUSS 4 D modules over a 16.5 D screw — the commonest layout,
        // and a starting point the engineer corrects from the machine card.
        barrelSections: [
          { lengthD: 4, heated: true, label: 'feed' },
          { lengthD: 4, heated: true },
          { lengthD: 4, heated: true },
          { lengthD: 4.5, heated: true },
        ],
        screwTempMode: 'controlled',
        dischargeSystem: 'cascade',
        dischargeCoupling: 'via-discharge-screw',
        provenance: 'default',
      },
      elements: seed?.elements ?? [
        makeElement('feed', 4),
        makeElement('kneading', 4),
        makeElement('restriction', 0.5),
        makeElement('kneading', 4),
        makeElement('vent', 2),
        makeElement('discharge', 2),
      ],
      compound: seed?.compound,
      rheology: seed?.rheology,
      operatingPoints: seed?.operatingPoints ?? [],
      calibration: seed?.calibration,
    });
    markDirty();
    emit('doc');
    return id;
  }

  /**
   * Change a machine — its card, its screw, its compound or its operating points.
   *
   * Whole sub-objects at a time, like `patchFormulation`, and coalesced into
   * one undo step per burst so typing a diameter is not six steps.
   */
  patchScrewConfig(id: string, patch: Partial<Omit<ScrewConfig, 'id'>>): void {
    const c = S.screwConfigs.find((x) => x.id === id);
    if (!c) return;
    const key = 'sc:' + id + ':' + Object.keys(patch).join(',');
    const now = Date.now();
    if (key !== this.lastProp.key || now - this.lastProp.t > 800) pushUndo();
    this.lastProp = { key, t: now };

    if (patch.line !== undefined) c.line = patch.line;
    if (patch.shapeId !== undefined) c.shapeId = patch.shapeId || undefined;
    if (patch.machine !== undefined) c.machine = { ...patch.machine };
    if (patch.elements !== undefined) {
      c.elements = patch.elements.map((e) => ({ ...e, barrel: { ...e.barrel } }));
    }
    if (patch.compound !== undefined) c.compound = { ...patch.compound };
    if (patch.rheology !== undefined) c.rheology = { ...patch.rheology };
    if (patch.operatingPoints !== undefined) {
      c.operatingPoints = patch.operatingPoints.map((o) => ({ ...o }));
    }
    if (patch.calibration !== undefined) c.calibration = { ...patch.calibration };
    markDirty();
    emit('doc');
  }

  /** Duplicate a machine — the usual way to set up a second, similar line. */
  duplicateScrewConfig(id: string): string | undefined {
    const c = S.screwConfigs.find((x) => x.id === id);
    if (!c) return undefined;
    pushUndo();
    const newId = 'sc' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    S.screwConfigs.push({
      ...c,
      id: newId,
      line: c.line ? c.line + ' copy' : '',
      shapeId: undefined,
      machine: { ...c.machine },
      elements: c.elements.map((e: ElementSpec) => ({ ...e, barrel: { ...e.barrel } })),
      compound: c.compound ? { ...c.compound } : undefined,
      rheology: c.rheology ? { ...c.rheology } : undefined,
      operatingPoints: c.operatingPoints?.map((o: OperatingPoint) => ({ ...o })) ?? [],
      calibration: c.calibration ? { ...c.calibration } : undefined,
    });
    markDirty();
    emit('doc');
    return newId;
  }

  /** Remove a machine. Nothing else points at one, so nothing needs unbinding. */
  deleteScrewConfig(id: string): void {
    if (!S.screwConfigs.some((c) => c.id === id)) return;
    pushUndo();
    S.screwConfigs = S.screwConfigs.filter((c) => c.id !== id);
    markDirty();
    emit('doc');
  }

  /**
   * Put the selected items on a line, or take them off it.
   *
   * Changing line clears what they were dosing: the materials belonged to the
   * old recipe, and carrying them over would leave a feeder dosing something
   * its line has never heard of.
   */
  setFormulationOf(formulationId: string): void {
    const shapes = S.selected
      .map((id) => S.shapes.find((x) => x.id === id))
      .filter((x): x is Shape => !!x);
    if (!shapes.length) return;
    pushUndo();
    for (const s of shapes) {
      if (s.formulationId === formulationId) continue;
      if (formulationId) s.formulationId = formulationId;
      else delete s.formulationId;
      delete s.feed;
    }
    markDirty();
    emit('doc');
    emit('selection');
  }

  /**
   * Replace what the selected item charges into the line.
   *
   * The whole list at once rather than one row at a time: a feed list is short,
   * and a caller that can only add and remove rows has to keep an index in step
   * with the model, which is where off-by-one edits to the wrong material come
   * from.
   *
   * Rows naming no material are dropped, and an empty list removes the field
   * entirely — so a shape that never had a formulation goes back to being one
   * that never had a formulation, and saves as it always did.
   */
  setFeed(rows: FeedItem[]): void {
    const shapes = S.selected
      .map((id) => S.shapes.find((x) => x.id === id))
      .filter((x): x is Shape => !!x);
    if (!shapes.length) return;
    // One undo step per burst, the way typing into a property field is one step.
    const key = S.selected.join(',') + ':feed';
    const now = Date.now();
    if (key !== this.lastProp.key || now - this.lastProp.t > 800) pushUndo();
    this.lastProp = { key, t: now };

    const clean = rows
      .filter((r) => r && r.part)
      .map((r) => {
        const out: FeedItem = { part: r.part };
        // A share of 100 is the default and is not written out, so the ordinary
        // one-feeder-per-material case leaves no number on the drawing at all.
        if (r.share !== undefined && r.share !== 100) out.share = Math.max(0, r.share);
        if (r.kgh !== undefined && r.kgh > 0) out.kgh = r.kgh;
        return out;
      });
    for (const s of shapes) {
      if (clean.length) s.feed = clean.map((r) => ({ ...r }));
      else delete s.feed;
    }
    markDirty();
    // 'doc' only. This changes the document, not what is selected, and the
    // properties form is keyed on the selection version — announcing a
    // selection change here remounts the form under the cursor, which costs
    // the focus after every single keystroke in the kg/h box.
    emit('doc');
  }

  /**
   * Name the finished good made at a NAMED item, rather than at the selection.
   *
   * The formulation sheet edits a line you are reading, not a shape you have
   * clicked — there may be three FG points on one drawing and no selection at
   * all. Selecting each one first would make naming a product a navigation
   * exercise.
   */
  setShapeFg(shapeId: string, code: string): void {
    const s = S.shapes.find((x) => x.id === shapeId);
    if (!s) return;
    const key = shapeId + ':fg';
    const now = Date.now();
    if (key !== this.lastProp.key || now - this.lastProp.t > 800) pushUndo();
    this.lastProp = { key, t: now };
    const trimmed = code.trim();
    if (trimmed) s.fg = trimmed;
    else delete s.fg;
    markDirty();
    emit('doc');
  }

  /** One item by id, for panels that edit a named shape rather than the selection. */
  getShape(shapeId: string): Shape | undefined {
    return S.shapes.find((x) => x.id === shapeId);
  }

  /** Select one item and centre nothing — used to jump from a sheet to a shape. */
  selectShape(shapeId: string): void {
    if (!S.shapes.some((x) => x.id === shapeId)) return;
    S.selected = [shapeId];
    markDirty();
    emit('selection');
  }

  /**
   * Where the finished good begins, as an explicit three-way choice.
   *
   * 'auto' removes the field so the symbol decides — a pelletizer starts one,
   * anything else does not. The other two pin it, which is what a line that
   * pellets somewhere unusual needs.
   */
  setFgStart(mode: 'auto' | 'on' | 'off'): void {
    const shapes = S.selected
      .map((id) => S.shapes.find((x) => x.id === id))
      .filter((x): x is Shape => !!x);
    if (!shapes.length) return;
    pushUndo();
    for (const s of shapes) {
      if (mode === 'auto') delete s.fgStart;
      else s.fgStart = mode === 'on';
    }
    markDirty();
    emit('doc');
    emit('selection');
  }

  setDocName(name: string): void {
    S.docMeta.docName = name;
    scheduleAutoSave();
    markDirty();
  }
  getDocMeta() {
    return S.docMeta;
  }

  // ── Shape creation (library) ────────────────────────────────────────
  /** Add a shape of `type` at a WORLD point and select it. */
  addShape(type: string, wx: number, wy: number): void {
    pushUndo();
    const s = mkShape(type, wx, wy);
    S.shapes.push(s);
    S.selected = [s.id];
    markDirty();
    emit('doc');
    emit('selection');
  }
  /** Add a shape at a client (screen) point — e.g. a drag-drop on the canvas. */
  addShapeAtClient(type: string, clientX: number, clientY: number): void {
    const r = getR();
    const wc = toW(clientX - r.left, clientY - r.top);
    this.addShape(type, wc.x, wc.y);
  }
  /** Cascade counter so successive tap-to-place shapes don't stack exactly. */
  private placeCount = 0;

  /** Add a shape at the centre of the current viewport (tap-to-place). */
  addShapeAtCenter(type: string): void {
    const wc = toW(S.CW / 2, S.CH / 2);
    const off = (this.placeCount++ % 6) * 24;
    this.addShape(type, wc.x + off, wc.y + off);
  }

  // ── Export / persistence ────────────────────────────────────────────
  save(): void {
    saveJson();
  }
  loadFile(file: File): void {
    loadJsonFile(file);
  }
  exportPng(): void {
    exportPng();
  }
  /**
   * Export the vector sheet set. Options come from the export dialog; calling
   * with none keeps the old one-click behaviour (auto scale on A1, landscape).
   */
  exportPdf(opts?: PdfExportOptions): void {
    void exportPdf(opts);
  }
  /**
   * Millimetres per drawing unit — the number every real dimension on the sheet
   * is derived from, so it is reachable from the 2D side too and not only from
   * the 3D panel where it used to live. Get this wrong and a plot marked 1:50 is
   * simply false, which is why the export dialog puts it on screen.
   */
  getScaleMm(): number {
    return S.scaleMm > 0 ? S.scaleMm : 25;
  }
  setScaleMm(v: number): void {
    S.scaleMm = Math.max(0.1, Math.min(5000, v));
    scheduleAutoSave();
    markDirty();
    emit('doc');
  }

  // ── Minimap ─────────────────────────────────────────────────────────
  toggleMinimap(): boolean {
    setMinimapCollapsed(!isMinimapCollapsed());
    return isMinimapCollapsed();
  }
  /** Centre the viewport on a WORLD point (minimap click). */
  centerOn(wx: number, wy: number): void {
    S.panX = -(wx - S.CW / S.zoom / 2) * S.zoom;
    S.panY = -(wy - S.CH / S.zoom / 2) * S.zoom;
    markDirty();
  }

  // ── Selection / status snapshots (for React) ────────────────────────
  getSelectionSummary(): SelectionSummary {
    const sel = S.selected;
    if (!sel.length) return { kind: 'none', count: 0 };
    const shape = S.shapes.find((x) => x.id === sel[0]);
    const connection = S.connections.find((x) => x.id === sel[0]);
    if (sel.length > 1) return { kind: 'multi', count: sel.length, shape, connection };
    if (shape) return { kind: 'shape', shape, count: 1 };
    if (connection) return { kind: 'connection', connection, count: 1 };
    return { kind: 'none', count: 0 };
  }

  getStatus(): StatusSnapshot {
    const summary = this.getSelectionSummary();
    let selLabel = '';
    if (summary.kind === 'multi') selLabel = `${summary.count} selected`;
    else if (summary.kind === 'shape') selLabel = summary.shape!.label || summary.shape!.type;
    else if (summary.kind === 'connection') selLabel = summary.connection!.label || 'connection';
    return {
      coordsX: Math.round(S.coordsX),
      coordsY: Math.round(S.coordsY),
      shapeCount: S.shapes.length,
      connCount: S.connections.length,
      tool: S.tool,
      selLabel,
    };
  }

  // ── Internal: label inline editing (engine-owned #label-input) ──────
  private openLabelEdit(s: Shape, _sx: number, _sy: number): void {
    this.editShape = s;
    // Zone Boxes get a multi-line textarea positioned OVER the box, so the user
    // writes paragraphs / bullet points inside it (Enter = new line).
    if (s.type === 'zonebox') {
      const ta = document.getElementById('zone-input') as HTMLTextAreaElement | null;
      if (!ta) return;
      const tl = toS(s.x, s.y);
      ta.style.left = tl.x + 'px';
      ta.style.top = tl.y + 'px';
      ta.style.width = s.w * S.zoom + 'px';
      ta.style.height = s.h * S.zoom + 'px';
      ta.style.fontSize = Math.max(9, (s.fontSize || 12) * S.zoom) + 'px';
      ta.style.display = 'block';
      // 'Zone Box' is the default placeholder label — start empty so the user types fresh.
      ta.value = s.label === 'Zone Box' ? '' : s.label || '';
      ta.focus();
      ta.select();
      return;
    }
    const li = document.getElementById('label-input') as HTMLInputElement | null;
    if (!li) return;
    const sp = toS(s.x + s.w / 2, s.y + s.h + 2);
    li.style.left = sp.x - 60 + 'px';
    li.style.top = sp.y + 'px';
    li.style.display = 'block';
    li.value = s.label || '';
    li.focus();
    li.select();
  }

  private bindLabelInput(): void {
    const commit = (el: HTMLInputElement | HTMLTextAreaElement) => {
      if (this.editShape) {
        pushUndo();
        this.editShape.label = el.value;
        this.editShape = null;
        markDirty();
        emit('selection');
      }
      el.style.display = 'none';
    };

    // Single-line editor for ordinary shape labels (Enter / Escape commit).
    const li = document.getElementById('label-input') as HTMLInputElement | null;
    if (li) {
      const commitLi = () => commit(li);
      const onKeyLi = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === 'Escape') li.blur();
      };
      li.addEventListener('blur', commitLi);
      li.addEventListener('keydown', onKeyLi);
      this.detachers.push(() => {
        li.removeEventListener('blur', commitLi);
        li.removeEventListener('keydown', onKeyLi);
      });
    }

    // Multi-line editor for Zone Boxes. Enter inserts a new line (native); so
    // does Ctrl+Enter. Escape or clicking away (blur) commits and closes.
    const ta = document.getElementById('zone-input') as HTMLTextAreaElement | null;
    if (ta) {
      const commitTa = () => commit(ta);
      const onKeyTa = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          ta.blur();
          return;
        }
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          // Plain Enter already adds a newline; make Ctrl/Cmd+Enter do the same.
          e.preventDefault();
          const a = ta.selectionStart;
          const b = ta.selectionEnd;
          ta.value = ta.value.slice(0, a) + '\n' + ta.value.slice(b);
          ta.selectionStart = ta.selectionEnd = a + 1;
        }
      };
      ta.addEventListener('blur', commitTa);
      ta.addEventListener('keydown', onKeyTa);
      this.detachers.push(() => {
        ta.removeEventListener('blur', commitTa);
        ta.removeEventListener('keydown', onKeyTa);
      });
    }
  }

  private bindMinimap(): void {
    const mm = document.getElementById('minimap-canvas') as HTMLCanvasElement | null;
    if (!mm) return;
    const onDown = (e: MouseEvent) => {
      if (isMinimapCollapsed()) return;
      const r = mm.getBoundingClientRect();
      const mx = e.clientX - r.left;
      const my = e.clientY - r.top;
      const wb = getWorldBounds();
      const W = mm.width;
      const H = mm.height;
      const sc = Math.min(W / wb.w, H / wb.h) * 0.92;
      const ox = (W - wb.w * sc) / 2 - wb.x * sc;
      const oy = (H - wb.h * sc) / 2 - wb.y * sc;
      this.centerOn((mx - ox) / sc, (my - oy) / sc);
    };
    mm.addEventListener('mousedown', onDown);
    this.detachers.push(() => mm.removeEventListener('mousedown', onDown));
  }

  /** A tiny starter diagram so first-run isn't a blank page. */
  private seedSample(): void {
    const silo = mkShape('silo', 220, 200);
    const tse = mkShape('tse', 460, 220);
    const winder = mkShape('winder', 720, 220);
    S.shapes.push(silo, tse, winder);
    S.connections.push(
      {
        id: 'c' + ++S.shapeCounter,
        fromId: silo.id,
        toId: tse.id,
        fromPort: 'E',
        toPort: 'W',
        label: '',
        lineStyle: 'solid',
        lineWidth: 1.5,
        color: '#1a1a1a',
        arrowHead: 'forward',
      },
      {
        id: 'c' + ++S.shapeCounter,
        fromId: tse.id,
        toId: winder.id,
        fromPort: 'E',
        toPort: 'W',
        label: '',
        lineStyle: 'solid',
        lineWidth: 1.5,
        color: '#1a1a1a',
        arrowHead: 'forward',
      },
    );
    markDirty();
  }

  /** Start a new empty document (confirms first if there are unsaved edits). */
  newDocument(): void {
    if (
      hasUnsavedChanges() &&
      !confirm('Start a new document? Unsaved changes will be lost.')
    )
      return;
    this.reset();
    toast('New document');
  }

  /** Reset the document to empty (used by a "New" action). Keeps the canvas binding. */
  reset(): void {
    const { canvas, ctx, wrap, CW, CH, dpr } = S;
    Object.assign(S, makeInitialState(), { canvas, ctx, wrap, CW, CH, dpr });
    this.lastProp = { key: '', t: 0 };
    clearAutoSave();
    markSaved();
    markDirty();
    emit('doc');
    emit('selection');
    emit('history');
    emit('view');
  }
}

/** The shared engine singleton. */
export const engine = new CanvasEngine();
